import { memo, type FC } from "react";
import { ActivityIndicator, Image, Pressable, View } from "react-native";
import "../../theme";
import { StyleSheet } from "react-native-unistyles";
import Animated from "react-native-reanimated";
import { Skeleton } from "../Skeleton";
import { VideoPlayerScrubBar } from "./parts";
import { useVideoPlayer } from "./useVideoPlayer";
import type { VideoPlayerProps } from "./VideoPlayer.types";

const SURFACE_FILL_STYLE = { width: "100%", height: "100%" } as const;
// `Skeleton` only accepts `style`, not a `pointerEvents` prop — merged into
// its style array instead (RN supports `pointerEvents` as a style property).
const POINTER_EVENTS_NONE = { pointerEvents: "none" } as const;

const VideoPlayer: FC<VideoPlayerProps> = memo((props) => {
  const {
    renderVideoSurface,
    playing = false,
    ended = false,
    muted = false,
    currentTime = 0,
    duration = 0,
    onSeek,
    onScrubStart,
    onScrubEnd,
    onPlayPause,
    onMuteToggle,
    onFullscreenToggle,
    onPrevious,
    onNext,
    hasPrevious = false,
    hasNext = false,
    renderPlayPauseButton,
    renderMuteButton,
    renderFullscreenButton,
    renderPreviousButton,
    renderNextButton,
    renderExitFullscreenButton,
    fullscreen = false,
    resetControlsKey,
    poster,
    loading = false,
    boxHeight,
    fillParent = false,
    maxHeight,
    style,
    testID,
  } = props;

  const state = useVideoPlayer({
    fullscreen,
    ended,
    resetControlsKey,
    boxHeight,
    fillParent,
    maxHeight,
    onPlayPause,
    onMuteToggle,
    onFullscreenToggle,
    onPrevious,
    onNext,
    onScrubStart,
    onScrubEnd,
  });

  return (
    <Pressable ref={state.boxRef} style={[styles.box, state.boxStyle, style]} testID={testID}>
      {/* Tap-to-toggle (touch) for the video surface only — a tap on a
          chrome button must not also toggle the surface, so this Pressable's
          onPress is scoped to just the surface, separate from the box-level
          hover tracking above (see useVideoPlayer's boxRef effect — hover is
          driven by a raw `mousemove` listener on this ref, not Pressable's
          onHoverIn/onHoverOut, which flicker when an overlaid chrome button's
          `pointer-events` toggles under a stationary cursor). */}
      <Pressable
        style={styles.surfacePressable}
        onPress={state.surfaceHandlers.onPress}
        accessibilityRole="button"
        accessibilityLabel="Toggle video controls"
      >
        {renderVideoSurface({ style: SURFACE_FILL_STYLE })}
      </Pressable>

      {/* While loading: a poster is the best preview we have, so it stays
          visible with only a transparent spinner floated over it. With no
          poster there is nothing to preview, so fall back to the opaque
          Skeleton placeholder. Both the poster and the Skeleton are
          `pointerEvents="none"` — they sit above the surface Pressable
          (later siblings paint on top) and would otherwise swallow taps,
          killing tap-to-toggle while loading, which is the *only* way to
          reveal chrome on touch (no hover fallback there). */}
      {loading &&
        (poster !== undefined ?
          <>
            {/* RN core `Image` has no `pointerEvents` prop/style (unlike View
                or expo-image) — wrap it in a non-interactive View instead. */}
            <View style={styles.poster} pointerEvents="none">
              <Image source={{ uri: poster }} style={styles.posterImage} resizeMode="contain" />
            </View>
            <View style={styles.loadingSpinnerOverlay} pointerEvents="none">
              <ActivityIndicator accessibilityLabel="Loading video" />
            </View>
          </>
        : <Skeleton
            variant="rectangular"
            style={[styles.loadingSkeleton, POINTER_EVENTS_NONE]}
            accessibilityLabel="Loading video"
          />)}

      {/* Centre row: previous / play-pause / next. The full-cover layer is a
          positioning shell only — `box-none` lets empty-area taps fall
          through to the surface Pressable's tap-to-toggle while the buttons
          inside stay hit-testable. Passed as a JSX prop (not via the
          StyleSheet): on web, react-native-web only honours "box-none"
          through the prop path — in a style it serialises to invalid CSS. */}
      <View style={styles.overlay} pointerEvents="box-none">
        <Animated.View style={[styles.centerRow, state.controlsAnimatedStyle]}>
          {renderPreviousButton?.({
            disabled: !hasPrevious,
            onPress: state.handlePrevious,
            animatedStyle: state.controlsAnimatedStyle,
          })}
          {renderPlayPauseButton?.({
            playing,
            ended,
            onPress: state.handlePlayPause,
            animatedStyle: state.controlsAnimatedStyle,
          })}
          {renderNextButton?.({
            disabled: !hasNext,
            onPress: state.handleNext,
            animatedStyle: state.controlsAnimatedStyle,
          })}
        </Animated.View>
      </View>

      {/* Bottom-right cluster: mute, then fullscreen (fullscreen sits at the
          very corner). Positioning and the fade animation are owned here —
          same reasoning as the centre row above — so consumers only ever
          supply button *content* via the render props, never corner offsets
          or z-index. */}
      <Animated.View style={[styles.bottomRightRow, state.controlsAnimatedStyle]}>
        {renderMuteButton?.({
          muted,
          onPress: state.handleMuteToggle,
          animatedStyle: state.controlsAnimatedStyle,
        })}
        {renderFullscreenButton?.({
          fullscreen,
          onPress: state.handleFullscreenToggle,
          animatedStyle: state.controlsAnimatedStyle,
        })}
      </Animated.View>

      {fullscreen && (
        <View style={styles.topRightSlot}>
          {renderExitFullscreenButton?.({ onPress: state.handleFullscreenToggle })}
        </View>
      )}

      {!fullscreen && (
        <VideoPlayerScrubBar
          currentTime={currentTime}
          duration={duration}
          onSeek={onSeek}
          onScrubStart={state.handleScrubStart}
          onScrubEnd={state.handleScrubEnd}
        />
      )}
    </Pressable>
  );
});

VideoPlayer.displayName = "VideoPlayer";

export { VideoPlayer };

const styles = StyleSheet.create((theme) => ({
  box: {
    width: "100%",
    position: "relative",
    alignSelf: "center",
    overflow: "hidden",
    borderRadius: theme.spacing.md,
    borderCurve: "continuous",
    backgroundColor: theme.colors.mediaOverlay.videoSurface,
  },
  surfacePressable: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
  poster: {
    position: "absolute",
    width: "100%",
    height: "100%",
    zIndex: 1,
  },
  posterImage: {
    width: "100%",
    height: "100%",
  },
  loadingSkeleton: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 2,
    borderRadius: 0,
  },
  loadingSpinnerOverlay: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    justifyContent: "center",
    alignItems: "center",
    zIndex: 2,
  },
  overlay: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    justifyContent: "center",
    alignItems: "center",
    zIndex: 9999,
  },
  centerRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: theme.spacing["5xl"],
  },
  bottomRightRow: {
    position: "absolute",
    bottom: theme.spacing.lg,
    right: theme.spacing.lg,
    flexDirection: "row",
    alignItems: "center",
    gap: theme.spacing.md,
    zIndex: 10000,
  },
  topRightSlot: {
    position: "absolute",
    top: theme.spacing.lg,
    right: theme.spacing.lg,
    zIndex: 10000,
  },
}));
